// ColorMatrix Class v1.2
//
// Author: Mario Klingemann
// http://www.quasimondo.com

// Changes in v1.1:
// Changed the RGB to luminance constants
// Added colorize() method

// Changes in v1.2:
// Added clone()
// Added randomize()
// Added blend()
// Added "filter" property

import ColorMatrixFilter = createjs.ColorMatrixFilter;
import ColorTransform = createjs.ColorTransform;

export class ColorMatrix {

    // RGB to Luminance conversion constants as found on
    // Charles A. Poynton's colorspace-faq:
    // http://www.faqs.org/faqs/graphics/colorspace-faq/

    /* 	private static let r_lum:Number = 0.212671;
     private static let g_lum:Number = 0.715160;
     private static let b_lum:Number = 0.072169;
     */

    // There seem  different standards for converting RGB
    // values to Luminance. This is the one by Paul Haeberli:

    private static r_lum: number = 0.3086;
    private static g_lum: number = 0.6094;
    private static b_lum: number = 0.0820;

    private static IDENTITY: any[] = [1, 0, 0, 0, 0,
        0, 1, 0, 0, 0,
        0, 0, 1, 0, 0,
        0, 0, 0, 1, 0];

    public matrix: any[];

    /*
     Function: ColorMatrix

     Constructor

     Parameters:

     mat - if omitted matrix gets initialized with an
     identity matrix. Alternatively it can be
     initialized with another ColorMatrix or
     an array (there is currently no check
     if the array is valid. A correct array
     contains 20 elements.)


     */

    constructor(mat: Object = null) {
        if (mat instanceof ColorMatrix) {
            this.matrix = mat.matrix.concat();
        }
        else if (mat instanceof Array) {
            this.matrix = mat.concat();
        }
        else {
            this.reset();
        }

    }

    /*
     Function: reset

     resets the matrix to the neutral identity matrix. Applying this
     matrix to an image will not make any changes to it.

     Parameters:

     none

     Returns:

     nothing


     */

    public reset(): void {
        this.matrix = ColorMatrix.IDENTITY.concat();
    }

    public clone(): ColorMatrix {
        return new ColorMatrix(this.matrix);
    }

    /*
     Function: adjustSaturation

     changes the saturation

     Parameters:

     s - typical values come in the range 0.0 ... 2.0 where
     0.0 means 0% Saturation
     0.5 means 50% Saturation
     1.0 is 100% Saturation (aka no change)
     2.0 is 200% Saturation

     Other values outside of this range are possible
     -1.0 will invert the hue but keep the luminance


     Returns:

     nothing


     */

    public adjustSaturation(s: number): void {
        let inv_s: number = 1 - s;

        let irlum: number = inv_s * ColorMatrix.r_lum;
        let iglum: number = inv_s * ColorMatrix.g_lum;
        let iblum: number = inv_s * ColorMatrix.b_lum;

        let mat: any[] = [irlum + s, iglum, iblum, 0, 0,
            irlum, iglum + s, iblum, 0, 0,
            irlum, iglum, iblum + s, 0, 0,
            0, 0, 0, 1, 0];

        this.concat(mat);
    }

    public adjustContrast(r: number, g: number, b: number): void {
        g = g || r;
        b = b || r;

        r += 1;
        g += 1;
        b += 1;

        let mat: any[] = [r, 0, 0, 0, 128 * (1 - r),
            0, g, 0, 0, 128 * (1 - g),
            0, 0, b, 0, 128 * (1 - b),
            0, 0, 0, 1, 0];

        this.concat(mat);
    }

    public adjustBrightness(r: number, g: number, b: number): void {
        g = g || r;
        b = b || r;

        let mat: any[] = [1, 0, 0, 0, r,
            0, 1, 0, 0, g,
            0, 0, 1, 0, b,
            0, 0, 0, 1, 0];

        this.concat(mat);

    }

    public adjustHue(angle: number): void {
        angle *= Math.PI / 180;

        let c: number = Math.cos(angle);
        let s: number = Math.sin(angle);

        let f1: number = 0.213;
        let f2: number = 0.715;
        let f3: number = 0.072;

        let mat: any[] = [(f1 + (c * (1 - f1))) + (s * (-f1)), (f2 + (c * (-f2))) + (s * (-f2)), (f3 + (c * (-f3))) + (s * (1 - f3)), 0, 0, (f1 + (c * (-f1))) + (s * 0.143), (f2 + (c * (1 - f2))) + (s * 0.14), (f3 + (c * (-f3))) + (s * -0.283), 0, 0, (f1 + (c * (-f1))) + (s * (-(1 - f1))), (f2 + (c * (-f2))) + (s * f2), (f3 + (c * (1 - f3))) + (s * f3), 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1];

        this.concat(mat);
    }

    public colorize(rgb: number, amount: number): void {

        let r: number = ((rgb >> 16) & 0xff) / 255;
        let g: number = ((rgb >> 8) & 0xff) / 255;
        let b: number = (rgb & 0xff) / 255;

        //if ( amount == null) amount = 1;
        let inv_amount: number = 1 - amount;

        let mat: any[] = [inv_amount + amount * r * ColorMatrix.r_lum, amount * r * ColorMatrix.g_lum, amount * r * ColorMatrix.b_lum, 0, 0,
        amount * g * ColorMatrix.r_lum, inv_amount + amount * g * ColorMatrix.g_lum, amount * g * ColorMatrix.b_lum, 0, 0,
        amount * b * ColorMatrix.r_lum, amount * b * ColorMatrix.g_lum, inv_amount + amount * b * ColorMatrix.b_lum, 0, 0,
            0, 0, 0, 1, 0];

        this.concat(mat);
    }

    public fill(rgb: number): void {
        let r: number = ((rgb >> 16) & 0xff);
        let g: number = ((rgb >> 8) & 0xff);
        let b: number = (rgb & 0xff);

        let mat: any[] = [0, 0, 0, 0, r,
            0, 0, 0, 0, g,
            0, 0, 0, 0, b,
            0, 0, 0, 1, 0];

        this.concat(mat);
    }

    public setAlpha(alpha: number): void {
        let mat: any[] = [1, 0, 0, 0, 0,
            0, 1, 0, 0, 0,
            0, 0, 1, 0, 0,
            0, 0, 0, alpha, 0];

        this.concat(mat);
    }

    public desaturate(): void {
        let mat: any[] = [ColorMatrix.r_lum, ColorMatrix.g_lum, ColorMatrix.b_lum, 0, 0,
        ColorMatrix.r_lum, ColorMatrix.g_lum, ColorMatrix.b_lum, 0, 0,
        ColorMatrix.r_lum, ColorMatrix.g_lum, ColorMatrix.b_lum, 0, 0,
            0, 0, 0, 1, 0];

        this.concat(mat);
    }

    public invert(): void {
        let mat: any[] = [1, 0, 0, 0, 255,
            0, -1, 0, 0, 255,
            0, 0, -1, 0, 255,
            0, 0, 0, 1, 0];

        this.concat(mat);
    }

    public threshold(t: number): void {
        let mat: any[] = [ColorMatrix.r_lum * 256, ColorMatrix.g_lum * 256, ColorMatrix.b_lum * 256, 0, -256 * t,
        ColorMatrix.r_lum * 256, ColorMatrix.g_lum * 256, ColorMatrix.b_lum * 256, 0, -256 * t,
        ColorMatrix.r_lum * 256, ColorMatrix.g_lum * 256, ColorMatrix.b_lum * 256, 0, -256 * t,
            0, 0, 0, 1, 0];
        this.concat(mat);
    }

    public randomize(amount: number): void {
        // if ( amount == null) amount = 1;

        let inv_amount: number = 1 - amount;

        let r1: number = inv_amount + amount * (Math.random() - Math.random());
        let g1: number = amount * (Math.random() - Math.random());
        let b1: number = amount * (Math.random() - Math.random());

        let o1: number = amount * 255 * (Math.random() - Math.random());

        let r2: number = amount * (Math.random() - Math.random());
        let g2: number = inv_amount + amount * (Math.random() - Math.random());
        let b2: number = amount * (Math.random() - Math.random());

        let o2: number = amount * 255 * (Math.random() - Math.random());

        let r3: number = amount * (Math.random() - Math.random());
        let g3: number = amount * (Math.random() - Math.random());
        let b3: number = inv_amount + amount * (Math.random() - Math.random());

        let o3: number = amount * 255 * (Math.random() - Math.random());

        let mat: any[] = [r1, g1, b1, 0, o1,
            r2, g2, b2, 0, o2,
            r3, g3, b3, 0, o3,
            0, 0, 0, 1, 0];

        this.concat(mat);
    }

    public setChannels(r: number, g: number, b: number, a: number): void {
        let rf: number = ((r & 1) == 1 ? 1 : 0) + ((r & 2) == 2 ? 1 : 0) + ((r & 4) == 4 ? 1 : 0) + ((r & 8) == 8 ? 1 : 0);
        if (rf > 0) {
            rf = 1 / rf;
        }
        let gf: number = ((g & 1) == 1 ? 1 : 0) + ((g & 2) == 2 ? 1 : 0) + ((g & 4) == 4 ? 1 : 0) + ((g & 8) == 8 ? 1 : 0);
        if (gf > 0) {
            gf = 1 / gf;
        }
        let bf: number = ((b & 1) == 1 ? 1 : 0) + ((b & 2) == 2 ? 1 : 0) + ((b & 4) == 4 ? 1 : 0) + ((b & 8) == 8 ? 1 : 0);
        if (bf > 0) {
            bf = 1 / bf;
        }
        let af: number = ((a & 1) == 1 ? 1 : 0) + ((a & 2) == 2 ? 1 : 0) + ((a & 4) == 4 ? 1 : 0) + ((a & 8) == 8 ? 1 : 0);
        if (af > 0) {
            af = 1 / af;
        }

        let mat: any[] = [(r & 1) == 1 ? rf : 0, (r & 2) == 2 ? rf : 0, (r & 4) == 4 ? rf : 0, (r & 8) == 8 ? rf : 0, 0,
        (g & 1) == 1 ? gf : 0, (g & 2) == 2 ? gf : 0, (g & 4) == 4 ? gf : 0, (g & 8) == 8 ? gf : 0, 0,
        (b & 1) == 1 ? bf : 0, (b & 2) == 2 ? bf : 0, (b & 4) == 4 ? bf : 0, (b & 8) == 8 ? bf : 0, 0,
        (a & 1) == 1 ? af : 0, (a & 2) == 2 ? af : 0, (a & 4) == 4 ? af : 0, (a & 8) == 8 ? af : 0, 0];

        this.concat(mat);

    }

    public blend(m: ColorMatrix, amount: number): void {
        let inv_amount: number = 1 - amount;

        for (let i: number = 0; i < 20; i++) {
            this.matrix[i] = inv_amount * this.matrix[i] + amount * m.matrix[i];
        }
    }

    public concat(mat: any[]): void {

        let temp: any[] = new Array();
        let i: number = 0;

        for (let y: number = 0; y < 4; y++) {

            for (let x: number = 0; x < 5; x++) {
                temp[i + x] = mat[i] * this.matrix[x] +
                    mat[i + 1] * this.matrix[x + 5] +
                    mat[i + 2] * this.matrix[x + 10] +
                    mat[i + 3] * this.matrix[x + 15] +
                    (x == 4 ? mat[i + 4] : 0);
            }
            i += 5;
        }

        this.matrix = temp;

    }

    public get filter(): ColorMatrixFilter {
        return new ColorMatrixFilter(this.matrix);
    }

    public get transformMatrix(): ColorTransform {
        return new ColorTransform(1, 1, 1, 1, this.matrix[4] / 255, this.matrix[9] / 255, this.matrix[14] / 255);
    }
}
